import { useRequest } from 'ahooks'
import { isError } from 'lodash-es'
import { startRegistration } from '@simplewebauthn/browser'

import useSignal from '$actions/useSignal'
import useMFABlock from '$actions/useMFABlock'
import use2FAHelper from '$actions/use2FAHelper'
import useCurrentAuthRTC from '$actions/useCurrentAuthRTC'

import { asyncSuccessResolver, toast } from '$app/utilities'
import { ToastMessage } from '$app/types'

import api from '$model/api'
import { errorMessageResolver, successResolver } from '$services/api/common'
import { MFARegisterPasskeyOptionRequest } from '$services/api/types'

export type UseSetupOptions = {
  onRegistered?: () => void
}

export type UseSetupReturn = ReturnType<typeof useSetup>

const toastID = 'passkey-register'

const setupReqToastMsg: ToastMessage = {
  id: toastID,
  loading: 'Registering...',
  error: e => {
    if (isError(e) && e.name === 'NotAllowedError') {
      return 'Registration timed out or was cancelled'
    }

    return errorMessageResolver(e, 'Failed to register')
  },
  success: 'Continued to verification..'
}

export const useSetup = ({ onRegistered }: UseSetupOptions) => {
  const { mfaAuth: apiInstance } = useSignal(api)
  const authRTC = useCurrentAuthRTC()
  const { mfaRegisterPasskeyOption, mfaRegisterPasskeyVerify } = apiInstance

  const doSetup = async (data: MFARegisterPasskeyOptionRequest) => {
    const optionsRes = await mfaRegisterPasskeyOption(data)
    const { data: options } = successResolver(optionsRes)

    const payloads = await startRegistration({ optionsJSON: options })

    return { payloads, options }
  }

  const doVerify = async (res: Awaited<ReturnType<typeof doSetup>>) => {
    if (!res) return

    const {
      payloads,
      options: { reference_code }
    } = res

    await toast
      .promise(
        verifyReq.runAsync({
          reference_code,
          payloads,
          mfa_token
        }),
        {
          id: toastID,
          loading: 'Verifying..',
          error: errorMessageResolver,
          success: 'Passkey registered.'
        }
      )
      .unwrap()
  }

  const setupReq = useRequest(doSetup, {
    manual: true,
    onSuccess: doVerify,
    onError: err => {
      if (twoFAHelper.is2FAError(err)) {
        mfaBlock.modalControls.open()
      }
    }
  })

  const verifyReq = useRequest(
    asyncSuccessResolver(mfaRegisterPasskeyVerify, successResolver),
    {
      manual: true,
      onSuccess: () => {
        onRegistered?.()
        reset()
      },
      onFinally: () => {
        mfaBlock.exit()
      }
    }
  )

  const requests = [setupReq, verifyReq]
  const isAnyLoading = requests.some(({ loading }) => loading)

  const twoFAHelper = use2FAHelper({ getMfaBlock: () => mfaBlock })
  const preferred_factor = twoFAHelper.getPreferredFactorByError(setupReq.error)
  const available_factors = twoFAHelper.getFactorsByError(setupReq.error)

  const mfaBlock = useMFABlock({
    apiInstance,
    authRTC,
    data: { available_factors, preferred_factor },
    onVerified: mfa_token => {
      const { runAsync } = setupReq

      // Auto retry the request with token
      toast
        .promise(runAsync({ mfa_token }), setupReqToastMsg)
        .unwrap()
        .catch(twoFAHelper.reChallengeOrExitOnError)
    }
  })

  const { mfa_token } = mfaBlock

  const reset = () => {
    requests.forEach(({ cancel, mutate }) => {
      cancel()
      mutate()
    })
  }

  const onSetup = () => {
    toast.promise(setupReq.runAsync({}), setupReqToastMsg)
  }

  return {
    onSetup,
    isAnyLoading,
    mfaBlock
  }
}

export default useSetup
